import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import { fetchAiWallets } from "../AICredits/actions";
import {
  fetchActivitySuggestions,
  fetchActivitySuggestionsSuccess,
  fetchActivitySuggestionsFailure,
  generateActivityPlanDone,
  setPlannerBusy,
} from "./actions";
import {
  FETCH_ACTIVITY_SUGGESTIONS,
  GENERATE_ACTIVITY_PLAN,
  ACCEPT_ACTIVITY_SUGGESTION,
  DISMISS_ACTIVITY_SUGGESTION,
  CONFIRM_DRAFT_ACTIVITY,
  DISCARD_DRAFT_ACTIVITY,
  REFINE_ACTIVITY_SUGGESTION,
} from "./constants";

const unwrap = (data) => data?.data ?? data ?? {};

export function* fetchActivitySuggestionsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/location/${locationId}/activity-planner/suggestions`,
    );
    const body = unwrap(data);
    yield put(
      fetchActivitySuggestionsSuccess({
        suggestions: body.suggestions ?? [],
        drafts: body.drafts ?? [],
        activityTypes: body.activity_types ?? [],
        builtAt: body.built_at ?? null,
        stale: body.stale ?? false,
      }),
    );
  } catch (e) {
    yield put(fetchActivitySuggestionsFailure());
  }
}

export function* generateActivityPlanSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, mode, activityType, force } = payload || {};
  try {
    const { brief, focus } = payload || {};
    const { data } = yield call(
      api(token).post,
      `/api/location/${locationId}/activity-planner/generate`,
      {
        mode: mode || "activity_first",
        activity_type: activityType ?? null,
        force: !!force,
        brief: brief || null,
        // Resident uuids the plan must re-engage — translated to anonymous
        // tokens server-side (names never enter the prompt).
        focus: Array.isArray(focus) && focus.length ? focus : null,
      },
      { timeout: 300000 },
    );
    const body = unwrap(data);
    yield put(
      generateActivityPlanDone({
        aiUnavailable: !!body.ai_unavailable,
        reason: body.reason ?? null,
      }),
    );
    // A live (non-cached, non-soft-failed) generate spent a credit — refresh
    // the wallet display, exactly as the care-plan AiSuggestions saga does.
    if (!body.ai_unavailable && !body.cached && locationId) {
      yield put(fetchAiWallets({ location_id: locationId }));
    }
    yield put(fetchActivitySuggestions({ locationId }));
    typeof onDone === "function" && onDone(body);
  } catch (e) {
    yield put(
      generateActivityPlanDone({ aiUnavailable: true, reason: "error" }),
    );
    yield put(errorAlert("Could not generate activity suggestions."));
  }
}

export function* acceptActivitySuggestionSaga({ payload, onAccepted }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, uuid, residents } = payload || {};
  yield put(setPlannerBusy(uuid));
  try {
    const { data } = yield call(
      api(token).post,
      `/api/location/${locationId}/activity-planner/suggestions/${uuid}/accept`,
      // Staff-adjusted roster (deselect picks / re-add a leave-out).
      Array.isArray(residents) && residents.length ? { residents } : {},
    );
    const body = unwrap(data);
    yield put(fetchActivitySuggestions({ locationId }));
    typeof onAccepted === "function" && onAccepted(body.activity_id);
  } catch (e) {
    yield put(setPlannerBusy(null));
    yield put(errorAlert("Could not accept the suggestion."));
  }
}

export function* dismissActivitySuggestionSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, uuid, reason } = payload || {};
  yield put(setPlannerBusy(uuid));
  try {
    yield call(
      api(token).post,
      `/api/location/${locationId}/activity-planner/suggestions/${uuid}/dismiss`,
      { reason: reason ?? null },
    );
    yield put(fetchActivitySuggestions({ locationId }));
  } catch (e) {
    yield put(setPlannerBusy(null));
    yield put(errorAlert("Could not dismiss the suggestion."));
  }
}

export function* confirmDraftActivitySaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, activityId, fields } = payload || {};
  try {
    yield call(
      api(token).post,
      `/api/location/${locationId}/activity-planner/activities/${activityId}/confirm`,
      fields,
    );
    yield put(successAlert("Activity confirmed and added to the programme."));
    typeof onDone === "function" && onDone(true);
  } catch (e) {
    if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Could not confirm the activity."));
    typeof onDone === "function" && onDone(false);
  }
}

export function* discardDraftActivitySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, activityId } = payload || {};
  try {
    yield call(
      api(token).post,
      `/api/location/${locationId}/activity-planner/activities/${activityId}/discard`,
    );
    yield put(fetchActivitySuggestions({ locationId }));
  } catch (e) {
    if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Could not discard the draft."));
  }
}

export function* refineActivitySuggestionSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, uuid, instruction } = payload || {};
  yield put(setPlannerBusy(uuid));
  try {
    const { data } = yield call(
      api(token).post,
      `/api/location/${locationId}/activity-planner/suggestions/${uuid}/refine`,
      { instruction },
      { timeout: 300000 },
    );
    const body = unwrap(data);
    if (body.ai_unavailable) {
      yield put(setPlannerBusy(null));
      yield put(
        errorAlert(
          body.reason === "out_of_credits"
            ? "AI planning is paused for this home — ask your administrator."
            : "Couldn't refine the suggestion — try again.",
        ),
      );
    } else if (locationId) {
      // A refine spent a credit — refresh the wallet display + the cards.
      yield put(fetchAiWallets({ location_id: locationId }));
      yield put(fetchActivitySuggestions({ locationId }));
    }
    typeof onDone === "function" && onDone(!body.ai_unavailable);
  } catch (e) {
    yield put(setPlannerBusy(null));
    yield put(errorAlert("Could not refine the suggestion."));
    typeof onDone === "function" && onDone(false);
  }
}

export default function* HomeActivityPlannerSaga() {
  yield all([
    takeLatest(FETCH_ACTIVITY_SUGGESTIONS, fetchActivitySuggestionsSaga),
    takeLatest(GENERATE_ACTIVITY_PLAN, generateActivityPlanSaga),
    takeEvery(ACCEPT_ACTIVITY_SUGGESTION, acceptActivitySuggestionSaga),
    takeEvery(DISMISS_ACTIVITY_SUGGESTION, dismissActivitySuggestionSaga),
    takeEvery(CONFIRM_DRAFT_ACTIVITY, confirmDraftActivitySaga),
    takeEvery(DISCARD_DRAFT_ACTIVITY, discardDraftActivitySaga),
    takeEvery(REFINE_ACTIVITY_SUGGESTION, refineActivitySuggestionSaga),
  ]);
}
